/**
 * `command-engine` — the ONE way to recover a 2D edit's AFFINE from the opaque
 * `(p) => p` callback the reducers hand around.
 *
 * Every in-place edit in this package arrives as a `TransformFn`: a function,
 * not a matrix. `translatePoint`, `rotatePoint`, `scalePoint`, `mirrorPoint`
 * and their compositions (the call sites in `apps/web/.../geometryReducerEdit
 * .ts` and `arrayReducers.ts`) are all AFFINE, and an affine on the plane is
 * determined by its value at three points — so three probes recover it
 * EXACTLY, with no fitting and no tolerance.
 *
 * Three call sites need that recovery and each used to inline the same six
 * subtractions: the ellipse frame transform (`ellipseEditTransform.ts`, which
 * needs the full `a…f` to build its explode matrix), the handedness test behind
 * the heavy-POLYLINE bulge rule (`geometryTransforms.ts`'s `linearPart`, which
 * needs `det`) and the OLE2FRAME refusal (`oleFrameTransformRefusal.ts`, which
 * needs to know whether the linear part is diagonal or anti-diagonal). One
 * domain truth, one implementation — the jscpd gate caught the third copy going
 * in, which is exactly what it is for.
 *
 * LEAF by construction: it imports nothing from this package, so it can be read
 * from any of those modules without a cycle (CLAUDE.md ПРИОРИТЕТ #2).
 */

/** A point on the plane — the coordinate pair every edit callback maps. */
export type Point2D = { x: number; y: number };

/**
 * The opaque `(p) => p` an in-place edit arrives as. EXPORTED from this leaf
 * (rather than re-declared beside each consumer) because it is the same
 * contract {@link probeAffine2d} documents: every caller that holds one of
 * these holds it in order to ask THIS module what affine it is. The refusal
 * predicates that read a map — `oleFrameTransformRefusal.ts`,
 * `tableMirrorRefusal.ts` — take their alias from here, which is also what
 * keeps the two sibling modules' preambles from being a jscpd clone of each
 * other (the gate caught it going in, exactly as this module's own header
 * records it catching the third copy of the probe itself).
 */
export type TransformFn = (p: Point2D) => Point2D;

/**
 * A plane affine `p ↦ (a·x + c·y + e, b·x + d·y + f)`.
 *
 * `{a, b}` is the image of the x basis vector and `{c, d}` the image of the y
 * one — i.e. the linear part is the COLUMN-major `[[a, c], [b, d]]` — and
 * `{e, f}` is the image of the origin. Naming them this way (rather than as a
 * nested matrix) is deliberate: it is the shape `ExplodeAffineMatrix` already
 * takes, so the ellipse path passes the result straight through.
 */
export interface Affine2D {
  /** x-image of the x basis vector. */
  readonly a: number;
  /** y-image of the x basis vector. */
  readonly b: number;
  /** x-image of the y basis vector. */
  readonly c: number;
  /** y-image of the y basis vector. */
  readonly d: number;
  /** x of the origin's image (the translation). */
  readonly e: number;
  /** y of the origin's image (the translation). */
  readonly f: number;
}

/**
 * Recover {@link Affine2D} from `fn` with three PURE probes.
 *
 * Exact for any affine `fn` up to the float error of the three subtractions.
 * For a NON-affine `fn` the result is the affine through those three points,
 * which is meaningless — no caller in this package passes one, and the contract
 * is stated here rather than guarded because a guard would need to probe a
 * fourth point and still could not prove affinity.
 */
export function probeAffine2d(fn: (p: Point2D) => Point2D): Affine2D {
  const o = fn({ x: 0, y: 0 });
  const ex = fn({ x: 1, y: 0 });
  const ey = fn({ x: 0, y: 1 });
  return {
    a: ex.x - o.x,
    b: ex.y - o.y,
    c: ey.x - o.x,
    d: ey.y - o.y,
    e: o.x,
    f: o.y,
  };
}
